import type { Project, TeamMember, TeamSummary } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, LogOut, Plus, Trash2, UsersRound, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { EmptyState, ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { allows, useOrgRole, useOrganizations } from '@/lib/roles';

/**
 * One team: who is on it, and what it looks after.
 *
 * Both lists are access. Adding someone here gives them every project below,
 * and adding a project here gives it to everyone above — which is why the page
 * puts the two next to each other rather than hiding either in a dialog.
 */
export function TeamPage() {
  const { teamId = '' } = useParams();
  const { client } = useAuth();

  const team = useQuery({ queryKey: ['team', teamId], queryFn: () => client.getTeam(teamId) });
  const organizations = useOrganizations();
  const organization = organizations.data?.find((org) => org.id === team.data?.org_id);

  if (team.isLoading) return <ListSkeleton rows={3} />;
  if (team.isError) {
    return (
      <EmptyState
        icon={<UsersRound className="size-5" />}
        title="Team not found"
        description="It may have been deleted, or you may not be a member of its organization."
      />
    );
  }
  if (!team.data) return null;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">{team.data.name}</h1>
        <p className="text-sm text-muted-foreground">
          A team in{' '}
          {organization ? (
            <Link
              to={`/organizations/${organization.id}/settings`}
              className="underline underline-offset-4"
            >
              {organization.name}
            </Link>
          ) : (
            'this organization'
          )}
          . Everyone on it can see every project it looks after.
        </p>
      </div>

      <Members team={team.data} />
      <Projects team={team.data} />
      <General team={team.data} />
    </div>
  );
}

function Members({ team }: { team: TeamSummary }) {
  const { client, user } = useAuth();
  const queryClient = useQueryClient();
  const role = useOrgRole(team.org_id);
  const canEdit = allows(role, 'admin');
  const [selected, setSelected] = useState('');

  const members = useQuery({
    queryKey: ['team-members', team.id],
    queryFn: () => client.listTeamMembers(team.id),
  });
  // Only an admin may change the roster, and only an admin can read this list,
  // so it is not fetched for anyone who could not act on it.
  const orgMembers = useQuery({
    queryKey: ['org-members', team.org_id],
    queryFn: () => client.listMembers(team.org_id),
    enabled: canEdit,
  });

  /** A roster change is an access change: what a project shows changes with it. */
  const changed = () => {
    void queryClient.invalidateQueries({ queryKey: ['team-members', team.id] });
    void queryClient.invalidateQueries({ queryKey: ['team', team.id] });
    void queryClient.invalidateQueries({ queryKey: ['teams', team.org_id] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
    void queryClient.invalidateQueries({ queryKey: ['project-members'] });
  };

  const add = useMutation({
    mutationFn: (userId: string) => client.addTeamMember(team.id, userId),
    onSuccess: () => {
      setSelected('');
      changed();
    },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => client.removeTeamMember(team.id, userId),
    onSuccess: changed,
  });

  const candidates =
    orgMembers.data?.filter(
      (candidate) => !members.data?.some((member) => member.id === candidate.id),
    ) ?? [];
  const error = add.error ?? remove.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UsersRound className="size-4" />
          Members
        </CardTitle>
        <CardDescription>
          People here can open every project the team looks after, without being added to each one.
          Admins and owners already see every project in the organization, so for them a team is a
          roster and nothing more.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {members.isLoading && <ListSkeleton rows={2} />}
        {members.isError && (
          <ErrorState error={members.error} onRetry={() => void members.refetch()} />
        )}
        {members.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">Nobody is on this team yet.</p>
        )}

        {members.data?.map((member) => (
          <MemberRow
            key={member.id}
            member={member}
            isSelf={member.id === user?.id}
            canEdit={canEdit}
            busy={remove.isPending}
            onRemove={() => {
              const leaving = member.id === user?.id;
              const question = leaving
                ? `Leave ${team.name}? You will lose access to the projects it gives you.`
                : `Take ${member.name} off ${team.name}?`;
              if (window.confirm(question)) remove.mutate(member.id);
            }}
          />
        ))}

        {canEdit && candidates.length > 0 && (
          <div className="flex items-center gap-2">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="w-full sm:w-72">
                <SelectValue placeholder="Choose someone" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((candidate) => (
                  <SelectItem key={candidate.id} value={candidate.id}>
                    {candidate.name} ({candidate.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => add.mutate(selected)}
              disabled={!selected || add.isPending}
            >
              <Plus />
              Add
            </Button>
          </div>
        )}

        {canEdit && orgMembers.data && candidates.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Everyone in the organization is already on this team.
          </p>
        )}

        {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}

function MemberRow({
  member,
  isSelf,
  canEdit,
  busy,
  onRemove,
}: {
  member: TeamMember;
  isSelf: boolean;
  canEdit: boolean;
  busy: boolean;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border px-3 py-2">
      {/* On a phone the controls drop below, rather than leaving the name no room. */}
      <div className="w-full min-w-0 sm:w-auto sm:flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{member.name}</span>
          {isSelf && <Badge variant="muted">you</Badge>}
        </div>
        <span className="block truncate text-xs text-muted-foreground">{member.email}</span>
      </div>

      <Badge variant="secondary">{member.role}</Badge>

      {/* Anyone may leave a team; taking somebody else off it takes an admin. */}
      {(canEdit || isSelf) &&
        (isSelf ? (
          <Button variant="outline" size="sm" onClick={onRemove} disabled={busy}>
            <LogOut />
            Leave
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            title={`Take ${member.name} off this team`}
            onClick={onRemove}
            disabled={busy}
          >
            <Trash2 />
          </Button>
        ))}
    </div>
  );
}

function Projects({ team }: { team: TeamSummary }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const role = useOrgRole(team.org_id);
  const canEdit = allows(role, 'admin');
  const [selected, setSelected] = useState('');

  const projects = useQuery({
    queryKey: ['team-projects', team.id],
    queryFn: () => client.listTeamProjects(team.id),
  });
  const visible = useQuery({
    queryKey: ['projects'],
    queryFn: () => client.listProjects(),
    enabled: canEdit,
  });

  const changed = (project: Project) => {
    void queryClient.invalidateQueries({ queryKey: ['team-projects', team.id] });
    void queryClient.invalidateQueries({ queryKey: ['team', team.id] });
    void queryClient.invalidateQueries({ queryKey: ['teams', team.org_id] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
    void queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    void queryClient.invalidateQueries({ queryKey: ['project-members', project.id] });
  };

  const assign = useMutation({
    mutationFn: ({ projectId, teamId }: { projectId: string; teamId: string | null }) =>
      client.setProjectTeam(projectId, teamId),
    onSuccess: (project) => {
      setSelected('');
      changed(project);
    },
  });

  // A project belongs to one team at a time, so the ones already here are the
  // only ones worth leaving out: choosing a project from another team moves it.
  const candidates =
    visible.data?.filter(
      (project) => project.org_id === team.org_id && project.team_id !== team.id,
    ) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Boxes className="size-4" />
          Projects
        </CardTitle>
        <CardDescription>
          {canEdit
            ? 'A project belongs to one team at a time. Giving it to this one takes it from whichever team had it, along with the access that came with it.'
            : 'What this team looks after. Being on the team is what lets you open these.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {projects.isLoading && <ListSkeleton rows={2} />}
        {projects.isError && (
          <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
        )}
        {projects.data?.length === 0 &&
          (canEdit || team.is_member ? (
            <p className="text-sm text-muted-foreground">
              This team looks after no projects yet, so being on it grants nothing.
            </p>
          ) : (
            // Not "there are none": a team's projects are listed only to the
            // people who can open them, and this reader is not one of them.
            <p className="text-sm text-muted-foreground">
              Nothing here you can open. A team&apos;s projects are listed only to the people who
              can already see them.
            </p>
          ))}

        {projects.data?.map((project) => (
          <div
            key={project.id}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Link
                  to={`/projects/${project.id}/issues`}
                  className="truncate text-sm font-medium hover:underline"
                >
                  {project.name}
                </Link>
                {!project.is_active && <Badge variant="muted">archived</Badge>}
              </div>
              <span className="block truncate text-xs text-muted-foreground">{project.slug}</span>
            </div>
            {canEdit && (
              <Button
                variant="ghost"
                size="icon"
                title={`Take ${project.name} off this team`}
                onClick={() => assign.mutate({ projectId: project.id, teamId: null })}
                disabled={assign.isPending}
              >
                <X />
              </Button>
            )}
          </div>
        ))}

        {canEdit && candidates.length > 0 && (
          <div className="flex items-center gap-2">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="w-full sm:w-72">
                <SelectValue placeholder="Choose a project" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                    {project.team_id ? ' (on another team)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => assign.mutate({ projectId: selected, teamId: team.id })}
              disabled={!selected || assign.isPending}
            >
              <Plus />
              Add
            </Button>
          </div>
        )}

        {assign.error && (
          <p className="text-sm text-destructive">{(assign.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

function General({ team }: { team: TeamSummary }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const role = useOrgRole(team.org_id);
  const canEdit = allows(role, 'admin');
  const [name, setName] = useState(team.name);

  const rename = useMutation({
    mutationFn: (next: string) => client.updateTeam(team.id, next),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team', team.id] });
      void queryClient.invalidateQueries({ queryKey: ['teams', team.org_id] });
    },
  });
  const remove = useMutation({
    mutationFn: () => client.deleteTeam(team.id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['team', team.id] });
      void queryClient.invalidateQueries({ queryKey: ['teams', team.org_id] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['project-members'] });
      navigate(`/organizations/${team.org_id}/settings`, { replace: true });
    },
  });

  if (!canEdit) return null;
  const error = rename.error ?? remove.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">General</CardTitle>
        <CardDescription>
          The slug is fixed, as it is for organizations and projects, so renaming a team does not
          change what refers to it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="team-name">Name</Label>
          <div className="flex gap-2">
            <Input
              id="team-name"
              className="max-w-sm"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Button
              variant="outline"
              onClick={() => rename.mutate(name)}
              disabled={!name.trim() || name.trim() === team.name || rename.isPending}
            >
              Save
            </Button>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Slug</Label>
          <code className="block text-sm text-muted-foreground">{team.slug}</code>
        </div>

        <div className="space-y-2 rounded-md border border-destructive/40 px-3 py-3">
          <p className="text-sm text-muted-foreground">
            Deleting a team keeps its projects and everything they have recorded. What goes is the
            access it granted: its members see its projects again only once someone adds them back,
            to a team or to each project.
          </p>
          <Button
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Delete ${team.name}? Its projects stay; its access does not.`)) {
                remove.mutate();
              }
            }}
          >
            <Trash2 />
            Delete team
          </Button>
        </div>

        {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}
